import {
  Button,
  Checkbox,
  Icon,
  Tooltip,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { dateLabelFormatDay, dateLabelFormatDayWithYear } from '@hero/constants';
import { Table, UserName } from '@hero/molecules';
import { useGetContacts, useGetTagList } from '@hero/services/contact-service/hooks';
import { checkFormatContainsYear } from '@hero/utils/dateUtils';

import { NewContactModal } from '../../NewContactModal';

import { CustomSelectionHead } from './CustomSelectionHead';
import * as S from './style';

function AddContactTable({
  selectedRows,
  disabledRows = {},
  disabledColumns = [],
  onChangeSelectedRows,
  onNewContactUpdatingDone,
  translations
}) {
  const { t } = useTranslation();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const [rowSelection, setRowSelection] = useState(selectedRows || {});
  const [openNewContactsModal, setOpenNewContactsModal] = useState(false);

  const { data: filterOptions } = useGetTagList();
  const { data: contacts, isLoading } = useGetContacts(
    {
      filters: {
        searchTerm: globalFilters.searchTerm,
        tags: globalFilters.filters?.map(filter => filter.value)
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      cacheTime: Infinity
    }
  );

  const rowSelectionArray = useMemo(() => {
    return Object.keys(rowSelection);
  }, [rowSelection]);

  const columns = React.useMemo(
    () =>
      [
        {
          id: 'select',
          maxSize: 30,
          header: ({ table }) => {
            const { disabledRows, rowSelection: selectedRows } = table.getState();

            const dRows = table.getRowModel().rows.filter(row => disabledRows[row.id]);
            const rowLength = table.getRowModel().rows.length;
            const disabled = rowLength === dRows.length;
            const checkedLength = table
              .getRowModel()
              .rows.filter(row => !disabledRows[row.id] && selectedRows[row.id]).length;
            const checked = checkedLength === rowLength - dRows.length;
            const indeterminate = checkedLength > 0 && checkedLength < rowLength - dRows.length;

            return (
              <Checkbox
                {...{
                  checked: disabled || checked,
                  disabled,
                  indeterminate,
                  onChange: e => {
                    table.toggleAllPageRowsSelected(e.target.checked);
                  }
                }}
              />
            );
          },
          cell: ({ row, table }) => {
            const { disabledRows } = table.getState();

            return (
              <Tooltip
                content={translations?.disabledRowsTooltipText}
                tooltipConfig={{ placement: 'top' }}
                active={!!disabledRows?.[row.id] || row.getIsSelected()}
              >
                <Checkbox
                  {...{
                    checked: !!disabledRows?.[row.id] || row.getIsSelected(),
                    disabled: !!disabledRows?.[row.id],
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler()
                  }}
                />
              </Tooltip>
            );
          }
        },
        {
          id: 'email',
          header: t('email'),
          size: 200,
          accessorKey: 'email',
          cell: ({
            row: {
              original: { email }
            }
          }) => <S.TableEmail>{email}</S.TableEmail>
        },
        {
          header: t('name'),
          size: 200,
          accessorKey: 'firstName',
          cell: ({
            row: {
              original: { firstName, lastName }
            }
          }) => <UserName firstName={firstName} lastName={lastName} />
        },
        {
          id: 'birthday',
          header: t('birthday'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.birthday?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'anniversary',
          header: t('workAnniversary'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.anniversary?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'tags',
          header: t('tags'),
          size: 350,
          accessorFn: originalRow => {
            return originalRow?.tags?.map(tag => ({
              label: tag.name,
              value: tag.id
            }));
          },
          type: 'tags'
        }
      ].filter(column => !disabledColumns.includes(column.id)),
    []
  );

  React.useEffect(() => {
    if (selectedRows && !isEqual(selectedRows, rowSelection)) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  React.useEffect(() => {
    if (onChangeSelectedRows) onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  const _toggleNewContactsModal = () => {
    setOpenNewContactsModal(prev => !prev);
  };

  return (
    <S.AddContactWrapper>
      <S.TableHeader>
        <S.TitleWrapper>
          <Typography variant="body2Bold">{t('contacts')}</Typography>
          <VerticalDivider $height="24px" $margin="0 12px" />
          <Typography variant="body2">
            <Trans
              i18nKey="totalContacts"
              values={{ count: contacts?.pagination?.total }}
              components={{ b: <b /> }}
            />
          </Typography>
        </S.TitleWrapper>
        <Button
          variant="text"
          onClick={() => {
            setOpenNewContactsModal(true);
          }}
          size="sm"
        >
          <Icon name="add" px="24px" space={5} />
          {t('addNewContacts')}
        </Button>
      </S.TableHeader>

      <Table
        columns={columns}
        data={contacts?.docs || []}
        globalFilters={globalFilters}
        state={{
          disabledRows
        }}
        pagination={{
          pageIndex,
          pageSize,
          total: contacts?.pagination?.total
        }}
        filterOptions={filterOptions?.docs?.map(doc => ({ label: doc.name, value: doc.id })) || []}
        manualPagination
        loading={isLoading}
        setGlobalFilters={setGlobalFilters}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        setPagination={setPagination}
        pageCount={contacts?.pagination?.pages}
        enabled={{ filter: true, header: true, perPageSelection: true }}
        translations={{
          selectedRowCountText: t('selectedRowCount', {
            selectedRowCount: rowSelectionArray.length,
            totalContactCount: contacts?.pagination?.total
          }),
          filteredWithText: t('filteredWithText', {
            filteredContactCount: contacts?.pagination?.total || '0'
          })
        }}
        filterProps={{
          placeholder: t('filterByType', { type: t('tags') }),
          searchPlaceholder: t('searchTagOnFilterPlaceholder')
        }}
        components={{
          SelectionHead: CustomSelectionHead
        }}
        enableRowSelection={row => {
          return !disabledRows?.[row.id];
        }}
        emptyTableContent={{
          feature: 'contacts',
          icon: 'contacts',
          createNew: _toggleNewContactsModal
        }}
      />

      <NewContactModal
        openModal={openNewContactsModal}
        onRequestClose={setOpenNewContactsModal}
        onUpdatingDone={onNewContactUpdatingDone}
      />
    </S.AddContactWrapper>
  );
}

export { AddContactTable };
